/**
 * PhaseMarkSkillEffect — Blades tree sunder path Fork 2-B active.
 *
 * Applies a spatial seam (phase_mark) to a target — a coherence
 * fracture that deals no damage but allows the next blade hit within
 * the mark window to bypass armor completely. If the mark expires
 * unused, refunds 40% cooldown.
 *
 * The mark is applied as a status effect and tracked internally for
 * consumption/expiry lifecycle. The actual armor bypass on the
 * follow-up hit is a CombatHitResolver concern at integration time;
 * this SkillEffect manages mark application, expiry timing, and
 * cooldown refund.
 *
 * Propagate upgrade: when a marked enemy dies, the mark migrates to
 * the nearest unmarked enemy within range. The spatial seam finds a
 * new host — remaining duration carries over. If no target exists,
 * the mark expires with the standard cooldown refund.
 *
 * Implements SkillEffect directly with own cooldown and mark timer.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Phase Mark, § Propagate
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { DeathTriggerConfig } from '../../../systems/DeathTriggerSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PhaseMarkDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
    }): void;
  };
  readonly cooldownHooks: {
    notifyCondition(conditionId: string): void;
  };
  readonly target: {
    getCurrentTargetId(): string;
  };
  readonly deathTriggers?: {
    register(config: DeathTriggerConfig): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PhaseMarkConfig {
  readonly cooldownMs: number;
  readonly baseMarkDurationMs: number;
  readonly cooldownRefundFraction: number;
}

const DEFAULT_CONFIG: PhaseMarkConfig = {
  cooldownMs: 1500,                 // long cooldown tier
  baseMarkDurationMs: 6000,         // 6s base mark duration
  cooldownRefundFraction: 0.4,      // 40% refund on unused expiry
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Record of a mark lifecycle for assertions. */
export interface MarkRecord {
  targetId: string;
  durationMs: number;
  consumed: boolean;
  expired: boolean;
  refundFired: boolean;
  propagated?: boolean;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PhaseMarkSkillEffect implements SkillEffect {
  readonly skillId = 'phase_mark';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: PhaseMarkDeps;
  private readonly config: PhaseMarkConfig;

  private _cooldownRemaining = 0;
  private _activationCount = 0;

  /** Active mark state. */
  private _markActive = false;
  private _markTargetId: string | null = null;
  private _markRemainingMs = 0;
  private _markDurationMs = 0;

  /** Lifecycle tracking for assertions. */
  private _markLog: MarkRecord[] = [];
  private _activeMarkCount = 0;

  /** Death trigger registration state. */
  private _deathTriggerRegistered = false;
  private _propagationCount = 0;

  constructor(deps: PhaseMarkDeps, config: Partial<PhaseMarkConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get markActive(): boolean { return this._markActive; }
  get markTargetId(): string | null { return this._markTargetId; }
  get markRemainingMs(): number { return this._markRemainingMs; }
  get markDurationMs(): number { return this._markDurationMs; }
  get markLog(): readonly MarkRecord[] { return this._markLog; }
  get activeMarkCount(): number { return this._activeMarkCount; }
  get deathTriggerRegistered(): boolean { return this._deathTriggerRegistered; }
  get propagationCount(): number { return this._propagationCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const targetId = this.deps.target.getCurrentTargetId();

    // Determine mark duration (base or upgrade override)
    const upgradeDuration = this.deps.resolver.getMaxEffect('phase_mark', 'phaseMarkDurationMs');
    const markDuration = upgradeDuration ?? this.config.baseMarkDurationMs;

    // Apply mark as status effect
    // STUB: deferred — no blade combo system consumes this mark
    this.deps.statusEffects.apply('phase_mark', {
      sourceSkillId: 'phase_mark',
      durationMs: markDuration,
    });

    // Track mark internally
    this._markActive = true;
    this._markTargetId = targetId;
    this._markRemainingMs = markDuration;
    this._markDurationMs = markDuration;
    this._activeMarkCount++;
    this._activationCount++;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    // Register death trigger for Propagate if enabled and not already registered
    this.registerDeathTriggerIfNeeded();

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick mark duration
    if (this._markActive) {
      this._markRemainingMs -= delta;
      if (this._markRemainingMs <= 0) {
        this.onMarkExpired();
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._markActive = false;
    this._markRemainingMs = 0;

    if (this._deathTriggerRegistered && this.deps.deathTriggers) {
      this.deps.deathTriggers.unregister('phase_mark_propagate');
      this._deathTriggerRegistered = false;
    }
  }

  // --- Mark lifecycle (called externally by CombatHitResolver at integration) ---

  /**
   * Called when a blade hit consumes the mark on the target.
   * The actual armor bypass is handled by CombatHitResolver —
   * this just tracks the consumption for lifecycle and refund logic.
   */
  consumeMark(targetId: string): boolean {
    if (!this._markActive || this._markTargetId !== targetId) return false;

    const record: MarkRecord = {
      targetId,
      durationMs: this._markDurationMs,
      consumed: true,
      expired: false,
      refundFired: false,
    };
    this._markLog.push(record);

    this._markActive = false;
    this._markTargetId = null;
    this._markRemainingMs = 0;
    this._activeMarkCount = Math.max(0, this._activeMarkCount - 1);

    return true;
  }

  // --- Propagate upgrade ---

  /**
   * Called by the integration layer when the marked target dies and
   * a valid propagation target has been identified. Migrates the mark
   * to the new target with remaining duration preserved. The active
   * mark count stays at 1 — this is migration, not duplication.
   *
   * Returns true if the mark was successfully migrated.
   */
  propagateMark(newTargetId: string): boolean {
    if (!this._markActive) return false;

    const remainingMs = this._markRemainingMs;
    const oldTargetId = this._markTargetId ?? '';

    // Log the migration on the old target
    this._markLog.push({
      targetId: oldTargetId,
      durationMs: this._markDurationMs,
      consumed: false,
      expired: false,
      refundFired: false,
      propagated: true,
    });

    // Apply mark to new target with remaining duration
    // STUB: deferred — no blade combo system consumes this mark
    this.deps.statusEffects.apply('phase_mark', {
      sourceSkillId: 'phase_mark',
      durationMs: remainingMs,
    });

    // Update internal tracking — mark count unchanged (migration)
    this._markTargetId = newTargetId;
    this._markRemainingMs = remainingMs;
    this._markDurationMs = remainingMs;
    this._propagationCount++;

    return true;
  }

  /**
   * Called by the integration layer when the marked target dies but
   * no valid propagation target exists within range. Forces mark
   * expiry with the standard cooldown refund.
   */
  forceMarkExpiry(): void {
    this.onMarkExpired();
  }

  /**
   * Returns the configured propagation range in tiles, or 0 if
   * Propagate is not unlocked. Used by the integration layer to
   * determine the search radius for nearby enemies.
   */
  get propagateRangeTiles(): number {
    if (!this.deps.resolver.hasEffect('phase_mark', 'propagateOnDeath')) return 0;
    return this.deps.resolver.getAdditiveEffect('phase_mark', 'propagateRangeTiles');
  }

  /**
   * Whether the Propagate upgrade is active.
   */
  get propagateEnabled(): boolean {
    return this.deps.resolver.hasEffect('phase_mark', 'propagateOnDeath');
  }

  // --- Internal ---

  private registerDeathTriggerIfNeeded(): void {
    if (this._deathTriggerRegistered) return;
    if (!this.deps.deathTriggers) return;
    if (!this.deps.resolver.hasEffect('phase_mark', 'propagateOnDeath')) return;

    const rangeTiles = this.deps.resolver.getAdditiveEffect(
      'phase_mark', 'propagateRangeTiles',
    );

    this.deps.deathTriggers.register({
      id: 'phase_mark_propagate',
      sourceSkillId: 'phase_mark',
      requireStatus: 'phase_mark',
      propagateStatus: {
        statusId: 'phase_mark',
        rangeTiles,
        intensityMultiplier: 1.0,
      },
    });

    this._deathTriggerRegistered = true;
  }

  private onMarkExpired(): void {
    if (!this._markActive) return;

    const record: MarkRecord = {
      targetId: this._markTargetId ?? '',
      durationMs: this._markDurationMs,
      consumed: false,
      expired: true,
      refundFired: true,
    };
    this._markLog.push(record);

    // Refund cooldown
    const refundMs = this.config.cooldownMs * this.config.cooldownRefundFraction;
    this._cooldownRemaining = Math.max(0, this._cooldownRemaining - refundMs);

    // Notify cooldown hooks for external systems
    this.deps.cooldownHooks.notifyCondition('phase_mark_expired_unused');

    this._markActive = false;
    this._markTargetId = null;
    this._markRemainingMs = 0;
    this._activeMarkCount = Math.max(0, this._activeMarkCount - 1);
  }
}
